import { ApiError } from "./api";

/**
 * CP-20 — candidate feedback after the test. The pure half of the Done-screen
 * feedback card: the length cap, the "is there anything to send" rule and the
 * error classifier. Kept out of the component so vitest covers it without
 * standing up React — the project's convention (see candidateError.ts).
 */

/**
 * Mirrors `CANDIDATE_FEEDBACK_MAX` in backend/api/candidate.py. Used as the
 * textarea's `maxLength`, so the candidate can never type what the API would
 * 422 on. Pinned by backend/tests/test_candidate_feedback.py.
 */
export const CANDIDATE_FEEDBACK_MAX = 2000;

/**
 * What would actually be sent: trimmed, or null when there is nothing to send
 * (blank) or the text is over the cap. The textarea already prevents the
 * latter; this is the backstop for a paste on a browser that ignores maxLength.
 */
export function normalizeFeedback(raw: string): string | null {
  const text = raw.trim();
  if (!text) return null;
  if (text.length > CANDIDATE_FEEDBACK_MAX) return null;
  return text;
}

export function feedbackCanSend(raw: string, pending: boolean): boolean {
  return !pending && normalizeFeedback(raw) !== null;
}

export type FeedbackFailure =
  | "rate_limited"
  | "closed"
  | "already_given"
  | "not_submitted"
  | "other";

/** FastAPI's structured errors carry a machine-readable `detail.reason`. */
function reasonOf(detail: unknown): unknown {
  return detail && typeof detail === "object"
    ? (detail as { reason?: unknown }).reason
    : undefined;
}

/**
 * Classify a failed `POST /api/t/{token}/feedback`. Reads `status` and the
 * structured `detail.reason` off ApiError — never the message text (the
 * `includes("409")` idiom matched any body that merely contained the digits;
 * see candidateError.ts for the two lockout bugs that taught us that).
 *
 * Two of these are NOT failures from the candidate's point of view and the
 * card renders them calmly: `closed` (the recruiter switched feedback off
 * after this screen loaded — the flag is read live) and `already_given` (a
 * double send or a stale tab; the first text is safe).
 */
export function classifyFeedbackError(err: unknown): FeedbackFailure {
  if (!(err instanceof ApiError)) return "other";
  if (err.status === 429) return "rate_limited";
  if (err.status === 409) {
    const reason = reasonOf(err.detail);
    if (reason === "feedback_disabled") return "closed";
    if (reason === "feedback_already_given") return "already_given";
    if (reason === "not_submitted") return "not_submitted";
  }
  return "other";
}

export function feedbackErrorMessage(kind: FeedbackFailure): string {
  switch (kind) {
    case "rate_limited":
      return "Too many tries just now — please wait a moment and try again.";
    case "not_submitted":
      return "Your test hasn't been submitted yet, so feedback can't be sent.";
    case "closed":
      return "Feedback is closed for this test.";
    case "already_given":
      return "Your feedback was already sent.";
    default:
      return "We couldn't send your feedback. Please try again.";
  }
}
